/**
 * @copyright   OnlyOneProd © 2025
 * @authors     Jonathan COULLET <contact@onlyoneprod.com>
 * @link        https://onlyoneprod.com
 * @notice      All information contained herein is,
 *              and remains the property of OnlyOneProd and its suppliers,
 *              Any dissemination of this information or reproduction
 *              of this material is strictly forbidden unless prior
 *              written permission is obtained from OnlyOneProd.
 */

export interface SpamDetectionResult {
    isSpam: boolean;
    reasons: string[];
    score: number;
}

export class SpamDetectionHelper {
    private static readonly vowels = 'aeiouAEIOU';
    private static readonly consonants = 'bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ';

    static detectSpam(text: string, fieldName = 'text'): SpamDetectionResult {
        const reasons: string[] = [];
        let score = 0;
        const cleanText = text.trim();

        if(cleanText.length === 0) {
            return {
                isSpam: false,
                reasons: [],
                score: 0
            };
        }

        const vowelConsonantRatio = this._checkVowelConsonantRatio(cleanText);
        if(vowelConsonantRatio.isAbnormal) {
            reasons.push(`Le ${fieldName} a un ratio voyelles/consonnes anormal`);
            score += 30;
        }
        
        const randomSequence = this._detectRandomSequences(cleanText);
        if(randomSequence.detected) {
            reasons.push(`Le ${fieldName} contient des séquences de caractères aléatoires`);
            score += 40;
        }

        const suspiciousPatterns = this._detectSuspiciousPatterns(cleanText);
        if(suspiciousPatterns.detected) {
            reasons.push(`Le ${fieldName} contient des patterns suspects`);
            score += 25;
        }

        const consecutiveChars = this._detectConsecutiveIdenticalChars(cleanText);
        if (consecutiveChars.detected) {
            reasons.push(`Le ${fieldName} contient trop de caractères identiques consécutifs`);
            score += 20;
        }

        const onlyAlphanumeric = this._isOnlyAlphanumericNoSpaces(cleanText);
        if(onlyAlphanumeric && cleanText.length > 10) {
            reasons.push(`Le ${fieldName} est une longue chaîne alphanumérique sans espaces`);
            score += 35;
        }

        return {
            isSpam: score >= 50,
            reasons,
            score
        };
    }

    private static _checkVowelConsonantRatio(text: string): { isAbnormal: boolean; ratio: number } {
        const vowelCount = text.split('').filter(char => this.vowels.includes(char)).length;
        const consonantCount = text.split('').filter(char => this.consonants.includes(char)).length;

        if(consonantCount === 0) {
            return {
                isAbnormal: vowelCount > 3,
                ratio: Infinity
            };
        }

        const ratio = vowelCount / consonantCount;
        const isAbnormal = ratio < 0.1 || ratio > 3.0;
        
        return {
            isAbnormal,
            ratio
        };
    }

    private static _detectRandomSequences(text: string): { detected: boolean; patterns: string[] } {
        const patterns: string[] = [];
        
        const noVowelSequences = text.match(/[bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]{6,}/g);
        if(noVowelSequences) {
            patterns.push(...noVowelSequences);
        }

        const alternatingCase = text.match(/([a-z][A-Z]){3,}|([A-Z][a-z]){3,}/g);
        if(alternatingCase) {
            patterns.push(...alternatingCase);
        }

        const randomPattern = /[A-Za-z]{10,}/g;
        const matches = text.match(randomPattern);
        if(matches) {
            for(const match of matches) {
                const uniqueChars = new Set(match.toLowerCase()).size;
                const randomnessRatio = uniqueChars / match.length;
                if(randomnessRatio > 0.7 && match.length > 12) {
                    patterns.push(match);
                }
            }
        }

        return {
            detected: patterns.length > 0,
            patterns
        };
    }

    private static _detectSuspiciousPatterns(text: string): { detected: boolean; reasons: string[] } {
        const reasons: string[] = [];

        if(text.length < 3) {
            reasons.push('Texte trop court');
        }

        if(text.length > 15 && text === text.toUpperCase() && /[A-Z]/.test(text)) {
            reasons.push('Texte entièrement en majuscules');
        }

        if(text.length > 20 && !text.includes(' ')) {
            reasons.push('Texte long sans espaces');
        }

        const specialCharCount = (text.match(/[^a-zA-Z0-9\s\-.,!?']/g) || []).length;
        if(specialCharCount > text.length * 0.3) {
            reasons.push('Trop de caractères spéciaux');
        }

        return {
            detected: reasons.length > 0,
            reasons
        };
    }

    private static _detectConsecutiveIdenticalChars(text: string): { detected: boolean; sequences: string[] } {
        const sequences: string[] = [];
        const consecutivePattern = /(.)\1{3,}/g;
        const matches = text.match(consecutivePattern);
        
        if(matches) {
            sequences.push(...matches);
        }

        return {
            detected: sequences.length > 0,
            sequences
        };
    }

    private static _isOnlyAlphanumericNoSpaces(text: string): boolean {
        return /^[a-zA-Z0-9]+$/.test(text);
    }

    static validateName(name: string): SpamDetectionResult {
        const result = this.detectSpam(name, 'nom');
        
        if(name.length > 50) {
            result.reasons.push('Nom trop long');
            result.score += 20;
        }

        if(/^\d+$/.test(name.trim())) {
            result.reasons.push('Le nom ne peut contenir que des chiffres');
            result.score += 40;
        }

        result.isSpam = result.score >= 50;
        return result;
    }

    static validateMessage(message: string): SpamDetectionResult {
        const result = this.detectSpam(message, 'message');
        
        if(message.length < 10) {
            result.reasons.push('Message trop court pour être significatif');
            result.score += 15;
        }

        result.isSpam = result.score >= 50;
        return result;
    }

    static validateContactForm(data: { name: string; email: string; message: string }): {
        isValid: boolean;
        errors: { [key: string]: string[] };
    } {
        const errors: { [key: string]: string[] } = {};

        const nameResult = this.validateName(data.name);
        if(nameResult.isSpam) {
            errors.name = nameResult.reasons;
        }

        const messageResult = this.validateMessage(data.message);
        if(messageResult.isSpam) {
            errors.message = messageResult.reasons;
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }
}
